import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import AnimatedGrid from "@/components/landing/AnimatedGrid";
import Reveal from "@/components/landing/Reveal";
import { renderTitle, toFullUrl } from "@/components/landing/utils";

/**
 * InvestorJourneySection — 7-step visual journey
 * Editable via content_blocks.investor_journey
 */
export default function InvestorJourneySection({ block }) {
  const steps = block?.steps || [];
  const [active, setActive] = useState(0);

  // Auto-advance every 6s (paused on interaction)
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused || !steps.length) return;
    const t = setInterval(() => setActive((a) => (a + 1) % steps.length), 6000);
    return () => clearInterval(t);
  }, [paused, steps.length]);

  if (!steps.length) return null;
  const current = steps[active];

  return (
    <section
      id="tu-experiencia"
      className="section-elev py-24 md:py-32 relative overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="gold-glow" style={{ width: 500, height: 500, top: 60, right: -160, opacity: 0.5 }} />
      <AnimatedGrid opacity={0.32} cellSize={64} />
      <div className="max-w-[1300px] mx-auto px-6 lg:px-10 relative">
        <div className="max-w-3xl mb-12">
          <Reveal><span className="eyebrow">{block?.eyebrow || "Tu experiencia como inversionista"}</span></Reveal>
          <Reveal delay={120}>
            <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl text-[#0A0A0A]">
              {renderTitle(block?.title || "Un proceso claro. Un acompañamiento <em>real</em>.")}
            </h2>
          </Reveal>
          {block?.subtitle && (
            <Reveal delay={220}>
              <p className="mt-5 text-[#3F3F46] text-base md:text-lg leading-relaxed max-w-2xl">{block.subtitle}</p>
            </Reveal>
          )}
        </div>

        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-8">
          {/* Steps rail */}
          <div className="relative">
            {/* Vertical connector line */}
            <div className="absolute left-[19px] top-3 bottom-3 w-px bg-gradient-to-b from-transparent via-black/10 to-transparent" />
            <div
              className="absolute left-[19px] top-3 w-px bg-gradient-to-b from-[#FFBF57] via-[#FFBF57] to-transparent transition-all duration-700"
              style={{ height: `${((active + 1) / steps.length) * 100}%` }}
            />
            <div className="space-y-2">
              {steps.map((s, i) => {
                const isActive = i === active;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { setActive(i); setPaused(true); }}
                    data-testid={`journey-step-${i}`}
                    className={`w-full flex items-center gap-4 px-3 py-3.5 rounded-xl text-left transition-all group ${isActive ? "bg-[#FFBF57]/8" : "hover:bg-black/[0.03]"}`}
                  >
                    <div className={`relative w-10 h-10 rounded-full flex items-center justify-center text-xs num-mono font-bold shrink-0 transition-all duration-500 ${isActive ? "bg-[#FFBF57] text-black shadow-[0_0_24px_rgba(255,191,87,0.5)]" : "bg-white border border-black/10 text-[#52525B] group-hover:border-[#FFBF57]/40"}`}>
                      {s.n || String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`text-[10px] uppercase tracking-widest font-mono transition-colors ${isActive ? "text-[#FFBF57]" : "text-[#52525B]"}`}>
                        {s.label || `Paso ${i + 1}`}
                      </div>
                      <div className={`font-display text-sm md:text-base mt-0.5 transition-colors ${isActive ? "text-[#0A0A0A]" : "text-[#0A0A0A]"}`}>
                        {s.title}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Active step detail */}
          <motion.div
            key={active}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="glass-elev p-6 md:p-10 corner-frame overflow-hidden"
          >
            {current.image_url && (
              <div className="-mx-6 -mt-6 md:-mx-10 md:-mt-10 mb-6 md:mb-8 aspect-[16/7] relative overflow-hidden bg-[#0A0A0A]" data-testid="journey-step-image">
                <img src={toFullUrl(current.image_url)} alt={current.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/60 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-4 text-[9px] uppercase tracking-[0.2em] font-mono text-[#FFBF57] bg-[#0A0A0A]/70 px-2.5 py-1 rounded-full">{current.label}</div>
              </div>
            )}
            <div className="flex items-baseline gap-4 mb-6">
              <div className="text-5xl md:text-7xl num-mono text-[#0A0A0A] leading-none">{current.n || String(active + 1).padStart(2, "0")}</div>
              <div className="text-[11px] uppercase tracking-widest text-[#52525B] font-mono">
                {active + 1} de {steps.length}
              </div>
            </div>
            <div className="font-display text-2xl md:text-3xl lg:text-4xl text-[#0A0A0A] mb-5 leading-tight">
              {current.title}
            </div>
            <p className="text-base md:text-lg text-[#3F3F46] leading-relaxed">{current.body}</p>

            {/* Progress dots */}
            <div className="mt-10 flex items-center gap-2" role="tablist">
              {steps.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setActive(i); setPaused(true); }}
                  aria-label={`Ir al paso ${i + 1}`}
                  className={`h-1 rounded-full transition-all duration-500 ${i === active ? "w-10 bg-[#FFBF57]" : "w-2 bg-black/15 hover:bg-black/30"}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
